import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Pagination } from "@/components/admin/pagination";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePageTitle } from "@/hooks/use-page-title";
import {
	type AiProductJob,
	type AiProductJobStatus,
	aiProductApi,
} from "@/lib/ai/ai-product-api";
import type { TagClass } from "@/lib/status";

const IMAGE_URL_RE =
	/https?:\/\/[^\s"'<>)\]]+?\.(?:png|jpe?g|webp|gif|avif)(?:\?[^\s"'<>)\]]*)?/gi;
// Séparateur de fiches dans un texte multi-produits (exports du scraper).
/**
 * Travaux affichés par page.
 *
 * Un lot plein en compte cent vingt : sans découpage, la liste demandait
 * cinquante entrées et taisait les autres — on ne pouvait ni les voir, ni
 * savoir qu'elles manquaient.
 */
const TAILLE_PAGE = 25;

const BULK_DELIMITER = /^\s*={4,}\s*PRODUIT\s*={4,}\s*$/m;
const BULK_SEP = "\n\n==================== PRODUIT ====================\n\n";

const STATUS_LABEL: Record<AiProductJobStatus, string> = {
	queued: "En file",
	running: "En cours",
	done: "Créé",
	failed: "Échec",
};
const STATUS_CLASS: Record<AiProductJobStatus, TagClass> = {
	queued: "neutral",
	running: "info",
	done: "success",
	failed: "danger",
};
const STEP_LABEL: Record<string, string> = {
	analyse: "Analyse IA du texte…",
	images: "Téléchargement des images…",
	création: "Création de la fiche…",
	variantes: "Variantes, offres & stock…",
	publication: "Publication…",
	terminé: "Terminé",
};

const FILTERS: Array<{ id: AiProductJobStatus | "all"; label: string }> = [
	{ id: "all", label: "Tous" },
	{ id: "queued", label: "En file" },
	{ id: "running", label: "En cours" },
	{ id: "done", label: "Créés" },
	{ id: "failed", label: "Échecs" },
];

function timeAgo(iso: string): string {
	const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
	if (s < 60) return "à l'instant";
	if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
	if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
	return new Date(iso).toLocaleDateString("fr-FR", {
		day: "2-digit",
		month: "short",
	});
}

/** Extrait un « titre » lisible du job (titre produit si connu, sinon début du texte). */
function jobTitle(j: AiProductJob): string {
	if (j.productTitle) return j.productTitle;
	const firstLine =
		j.rawText.split("\n").find((l) => l.trim().length > 3) ?? j.rawText;
	return firstLine.trim().slice(0, 80);
}

function InfoRow({ k, v }: { k: string; v: string }) {
	return (
		<div style={{ display: "flex", gap: 10 }}>
			<span className="muted" style={{ minWidth: 130, fontSize: 12 }}>
				{k}
			</span>
			<span style={{ fontWeight: 500, fontSize: 12.5 }}>{v}</span>
		</div>
	);
}

/** Panneau de détails d'un job : avertissements lisibles + résumé de la fiche + erreur. */
function JobDetailsModal({
	job,
	onClose,
	onView,
}: {
	job: AiProductJob;
	onClose: () => void;
	onView: (id: string) => void;
}) {
	const d = (job.aiDraft ?? {}) as {
		title?: string;
		categorySlug?: string;
		basePrice?: number;
		compareAtPrice?: number | null;
		brand?: string | null;
		variants?: unknown[];
		specs?: unknown[];
		options?: Array<{
			type?: string;
			name?: string;
			values?: Array<{ label?: string; color?: string; mediaId?: string }>;
		}>;
	};
	const colorOpt = Array.isArray(d.options)
		? d.options.find((o) => o?.type === "color")
		: undefined;
	const fmt = (n?: number | null) =>
		n ? Number(n).toLocaleString("fr-FR") : "—";

	return (
		<Modal
			title={jobTitle(job)}
			subtitle={`${STATUS_LABEL[job.status]} · ${timeAgo(job.createdAt)}${d.brand ? ` · ${d.brand}` : ""}`}
			onClose={onClose}
			width={580}
		>
			{job.warnings.length > 0 && (
				<div style={{ marginBottom: 14 }}>
					<div
						className="muted"
						style={{
							fontSize: 11,
							textTransform: "uppercase",
							letterSpacing: 0.4,
							marginBottom: 6,
						}}
					>
						{job.warnings.length} avertissement
						{job.warnings.length > 1 ? "s" : ""}
					</div>
					{job.warnings.map((w, i) => (
						<div
							key={i}
							style={{
								display: "flex",
								gap: 8,
								alignItems: "flex-start",
								padding: "8px 10px",
								background: "#fff7ea",
								border: "1px solid #f0d9a8",
								borderRadius: 8,
								marginBottom: 6,
								fontSize: 12.5,
								lineHeight: 1.45,
							}}
						>
							<span style={{ color: "var(--a-warning, #b7791f)" }}>⚠</span>
							<span>{w}</span>
						</div>
					))}
				</div>
			)}

			{job.status === "failed" && job.error && (
				<div
					style={{
						marginBottom: 14,
						padding: "8px 10px",
						background: "#fdecec",
						border: "1px solid #f2c4c4",
						borderRadius: 8,
						fontSize: 12.5,
						color: "var(--a-danger)",
						lineHeight: 1.45,
					}}
				>
					{job.error}
				</div>
			)}

			{d.title ? (
				<div style={{ display: "grid", gap: 8 }}>
					<InfoRow k="Catégorie choisie" v={d.categorySlug ?? "—"} />
					<InfoRow
						k="Prix"
						v={`${fmt(d.basePrice)} FCFA${d.compareAtPrice ? ` (barré ${fmt(d.compareAtPrice)})` : ""}`}
					/>
					<InfoRow k="Images" v={String(job.imageUrls.length)} />
					<InfoRow
						k="Variantes"
						v={String(Array.isArray(d.variants) ? d.variants.length : 0)}
					/>
					<InfoRow
						k="Caractéristiques"
						v={String(Array.isArray(d.specs) ? d.specs.length : 0)}
					/>
					{colorOpt && Array.isArray(colorOpt.values) && (
						<div>
							<div className="muted" style={{ fontSize: 12, marginBottom: 5 }}>
								Couleurs (📷 = image associée)
							</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
								{colorOpt.values.map((v, i) => (
									<span
										key={i}
										style={{
											display: "inline-flex",
											alignItems: "center",
											gap: 6,
											border: "1px solid var(--a-border)",
											borderRadius: 6,
											padding: "3px 8px",
											fontSize: 12,
										}}
									>
										{v.color && (
											<span
												style={{
													width: 12,
													height: 12,
													borderRadius: "50%",
													background: v.color,
													border: "1px solid rgba(0,0,0,0.15)",
												}}
											/>
										)}
										{v.label}
										{v.mediaId && <span title="image associée">📷</span>}
									</span>
								))}
							</div>
						</div>
					)}
				</div>
			) : (
				job.status !== "failed" && (
					<p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
						Analyse en cours…
					</p>
				)
			)}

			{job.productId && (
				<ModalFooter>
					<AdminButton variant="primary" onClick={() => onView(job.productId!)}>
						<AdminIcon n="eye" s={14} />
						Voir le produit
					</AdminButton>
				</ModalFooter>
			)}
		</Modal>
	);
}


/**
 * Création de la catégorie qui a fait échouer une fiche.
 *
 * Les exports de fournisseurs couvrent des rayons que l'arborescence ne prévoit
 * pas encore. Sans cet écran, chaque manque imposait d'aller créer la catégorie
 * ailleurs, à la main, puis de revenir relancer — pour un import de plusieurs
 * centaines de fiches, autant d'allers-retours.
 *
 * La proposition de l'IA est modifiable avant création : une catégorie apparaît
 * dans le menu public et dans l'arborescence, la fabriquer sans relecture
 * peuplerait vite le site de doublons mal placés.
 */
function CreateCategoryModal({
	job,
	onClose,
	onCreated,
}: {
	job: AiProductJob;
	onClose: () => void;
	onCreated: (relances: number) => void;
}) {
	const toast = useToast();
	const { data, isLoading, error } = useQuery({
		queryKey: ["ai-jobs", "suggest-category", job.id],
		queryFn: () => aiProductApi.suggestCategory(job.id),
		retry: false,
	});

	const [form, setForm] = useState<{
		name: string;
		description: string;
		metaTitle: string;
		metaDescription: string;
	} | null>(null);

	useEffect(() => {
		if (!data) return;
		setForm({
			name: data.name,
			description: data.description,
			metaTitle: data.metaTitle,
			metaDescription: data.metaDescription,
		});
	}, [data]);

	const creer = useMutation({
		mutationFn: () =>
			aiProductApi.createMissingCategory({
				// Le slug reste celui que l'analyse réclamait : c'est à lui que les
				// fiches en échec se raccrocheront à la relance.
				slug: data!.slug,
				name: form!.name,
				parentId: data!.parentId,
				description: form!.description,
				metaTitle: form!.metaTitle,
				metaDescription: form!.metaDescription,
				keywords: data!.keywords,
			}),
		onSuccess: (r) => {
			toast.success(
				r.relances > 1
					? `Catégorie créée — ${r.relances} fiches relancées.`
					: "Catégorie créée — fiche relancée.",
			);
			onCreated(r.relances);
		},
		onError: (e) => toast.error(extractErrorMessage(e, "Création impossible")),
	});

	return (
		<Modal
			title="Créer la catégorie manquante"
			subtitle={data ? `Demandée par l’analyse : ${data.slug}` : undefined}
			onClose={onClose}
			width={560}
		>
			{isLoading && <p className="muted" style={{ fontSize: 13 }}>L’assistant prépare une proposition…</p>}
			{error && (
				<p style={{ fontSize: 13, color: "var(--a-danger)" }}>
					{extractErrorMessage(error, "Proposition impossible")}
				</p>
			)}
			{data && form && (
				<>
					<div style={{ marginBottom: 12 }}>
						<label style={lbl}>Nom affiché</label>
						<input
							style={inp}
							value={form.name}
							onChange={(e) => setForm({ ...form, name: e.target.value })}
						/>
					</div>
					<div style={{ marginBottom: 12 }}>
						<label style={lbl}>Rattachée à</label>
						<div
							style={{
								...inp,
								background: "var(--a-bg-2)",
								color: data.parentPath ? "var(--a-ink)" : "var(--a-danger)",
							}}
						>
							{data.parentPath ?? "Aucun parent — la catégorie sera créée à la racine"}
						</div>
					</div>
					<div style={{ marginBottom: 12 }}>
						<label style={lbl}>Description</label>
						<textarea
							style={{ ...inp, minHeight: 64, resize: "vertical" }}
							value={form.description}
							onChange={(e) => setForm({ ...form, description: e.target.value })}
						/>
					</div>
					<div style={{ marginBottom: 12 }}>
						<label style={lbl}>Titre de référencement</label>
						<input
							style={inp}
							value={form.metaTitle}
							onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
						/>
					</div>
					<div style={{ marginBottom: 12 }}>
						<label style={lbl}>Description de référencement</label>
						<textarea
							style={{ ...inp, minHeight: 56, resize: "vertical" }}
							value={form.metaDescription}
							onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
						/>
					</div>
					{data.jobsEnAttente > 1 && (
						<p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
							{data.jobsEnAttente} fiches attendent cette catégorie : toutes seront
							relancées.
						</p>
					)}
					<ModalFooter>
						<AdminButton variant="ghost" onClick={onClose}>
							Annuler
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={() => creer.mutate()}
							disabled={creer.isPending || !form.name.trim()}
						>
							{creer.isPending ? "Création…" : "Créer et relancer"}
						</AdminButton>
					</ModalFooter>
				</>
			)}
		</Modal>
	);
}

const lbl: React.CSSProperties = {
	display: "block",
	fontSize: 12,
	fontWeight: 600,
	color: "var(--a-ink-2)",
	marginBottom: 4,
};

const inp: React.CSSProperties = {
	width: "100%",
	padding: "8px 10px",
	border: "1px solid var(--a-border)",
	borderRadius: 6,
	fontSize: 13,
	background: "var(--a-bg)",
	color: "var(--a-ink)",
	outline: "none",
};

export function AdminCatalogAiPage() {
	usePageTitle("Assistant IA · Catalogue");
	const navigate = useNavigate();
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();

	const [rawText, setRawText] = useState("");
	const [autoPublish, setAutoPublish] = useState(false);
	const [filter, setFilter] = useState<AiProductJobStatus | "all">("all");
	const [detail, setDetail] = useState<AiProductJob | null>(null);
	const fileRef = useRef<HTMLInputElement>(null);

	const detectedImages = useMemo(
		() => [...new Set(rawText.match(IMAGE_URL_RE) ?? [])],
		[rawText],
	);
	// Nombre de produits détectés (découpage sur le séparateur). 1 = produit unique.
	const productCount = useMemo(() => {
		if (rawText.trim().length < 20) return 0;
		return rawText
			.split(BULK_DELIMITER)
			.map((b) => b.trim())
			.filter((b) => b.length >= 20).length;
	}, [rawText]);
	const isBulk = productCount > 1;

	const { data: stats } = useQuery({
		queryKey: ["ai-jobs", "stats"],
		queryFn: () => aiProductApi.stats(),
		refetchInterval: 4000,
	});
	const hasActive = (stats?.queued ?? 0) + (stats?.running ?? 0) > 0;
	// Fiche dont on est en train de créer la catégorie manquante.
	const [categorieAcreer, setCategorieAcreer] = useState<AiProductJob | null>(null);
	const [page, setPage] = useState(1);
	const { data: list } = useQuery({
		queryKey: ["ai-jobs", "list", filter, page],
		queryFn: () =>
			aiProductApi.list({
				...(filter === "all" ? {} : { status: filter }),
				page,
				pageSize: TAILLE_PAGE,
			}),
		refetchInterval: hasActive ? 2500 : 8000,
		// Conserve la page affichée pendant le rechargement : sans cela, la liste
		// se vide à chaque rafraîchissement automatique, toutes les 2,5 secondes
		// quand un lot tourne.
		placeholderData: (prev) => prev,
	});
	const jobs = list?.data ?? [];
	const total = list?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / TAILLE_PAGE));

	// Un filtre plus étroit peut rendre la page courante inexistante : on revient
	// au début plutôt que d'afficher un écran vide qui semblerait dire « aucun
	// résultat ».
	useEffect(() => {
		setPage(1);
	}, [filter]);
	useEffect(() => {
		if (page > totalPages) setPage(totalPages);
	}, [page, totalPages]);

	const invalidate = () => void qc.invalidateQueries({ queryKey: ["ai-jobs"] });

	const createMut = useMutation({
		mutationFn: () => aiProductApi.create({ rawText, autoPublish }),
		onSuccess: () => {
			setRawText("");
			toast.success(
				"Création lancée — suis la progression dans la file ci-dessous.",
			);
			invalidate();
		},
		onError: (e) => toast.error(extractErrorMessage(e, "Lancement impossible")),
	});
	const bulkMut = useMutation({
		mutationFn: () => aiProductApi.createBulk({ rawText, autoPublish }),
		onSuccess: (r) => {
			setRawText("");
			const d = r as typeof r & { dejaImportes?: number; filePleine?: number };
			const deja = d.dejaImportes ?? 0;
			const pleine = d.filePleine ?? 0;

			// Rien de neuf : le dire clairement plutôt que d'annoncer « 0 mis en
			// file », qui laisserait croire à une panne.
			if (r.queued === 0 && deja > 0) {
				toast.success(
					`Rien à importer : ${deja} fiche${deja > 1 ? "s" : ""} déjà au catalogue.`,
				);
				invalidate();
				return;
			}

			const details: string[] = [];
			if (deja > 0) details.push(`${deja} déjà importée${deja > 1 ? "s" : ""}`);
			// Distinguer les deux causes : la première ne demande rien, la seconde
			// exige de recharger le fichier plus tard.
			if (pleine > 0)
				details.push(
					`${pleine} en attente de place — recharge le fichier quand la file se vide`,
				);

			toast.success(
				`${r.queued} produit${r.queued > 1 ? "s" : ""} mis en file` +
					(details.length ? ` · ${details.join(" · ")}` : "") +
					" — suis la progression ci-dessous.",
			);
			invalidate();
		},
		onError: (e) =>
			toast.error(extractErrorMessage(e, "Lancement du lot impossible")),
	});
	const launching = createMut.isPending || bulkMut.isPending;
	const retryMut = useMutation({
		mutationFn: (id: string) => aiProductApi.retry(id),
		onSuccess: invalidate,
		onError: (e) => toast.error(extractErrorMessage(e, "Relance impossible")),
	});
	const removeMut = useMutation({
		mutationFn: (id: string) => aiProductApi.remove(id),
		onSuccess: invalidate,
		onError: (e) =>
			toast.error(extractErrorMessage(e, "Suppression impossible")),
	});

	const launch = () => {
		if (rawText.trim().length < 20) {
			toast.error("Colle au moins quelques lignes décrivant le produit.");
			return;
		}
		if (isBulk) bulkMut.mutate();
		else createMut.mutate();
	};

	const importFiles = async (files: FileList | null) => {
		if (!files?.length) return;
		try {
			const texts = await Promise.all([...files].map((f) => f.text()));
			const joined = texts
				.map((t) => t.trim())
				.filter(Boolean)
				.join(BULK_SEP);
			setRawText((prev) =>
				prev.trim() ? prev.trimEnd() + BULK_SEP + joined : joined,
			);
			toast.success(
				`${files.length} fichier${files.length > 1 ? "s" : ""} importé${files.length > 1 ? "s" : ""}.`,
			);
		} catch {
			toast.error("Lecture du/des fichier(s) impossible.");
		} finally {
			if (fileRef.current) fileRef.current.value = "";
		}
	};

	const removeJob = async (j: AiProductJob) => {
		const ok = await confirm({
			title: "Retirer de la file",
			message: (
				<>
					« {jobTitle(j)} » sera retiré de l'historique.{" "}
					{j.productId ? "Le produit créé est conservé." : ""}
				</>
			),
			confirmLabel: "Retirer",
			tone: "danger",
		});
		if (ok) removeMut.mutate(j.id);
	};

	return (
		<>
			<PageHead
				title={
					<span
						style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
					>
						<span
							style={{
								display: "inline-grid",
								placeItems: "center",
								width: 34,
								height: 34,
								borderRadius: 10,
								background:
									"linear-gradient(135deg, var(--a-terracotta), var(--a-ochre, #D4A24C))",
								color: "#fff",
							}}
						>
							<AdminIcon n="star" s={18} />
						</span>
						Assistant IA — Création de produits
					</span>
				}
				sub="Colle une description brute (avec les liens des images) : l'IA crée la fiche complète en arrière-plan."
				actions={
					<AdminButton variant="ghost" onClick={() => navigate("/catalogue")}>
						<AdminIcon n="box" s={14} />
						Retour au catalogue
					</AdminButton>
				}
			/>

			{/* KPIs */}
			<div
				className="kpi-grid"
				style={{ gridTemplateColumns: "repeat(5, 1fr)" }}
			>
				<div className="kpi accent">
					<div className="kpi-l">Créés aujourd'hui</div>
					<div className="kpi-v tn">{stats?.doneToday ?? 0}</div>
					<div className="kpi-foot muted">objectif 400 / jour</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">En file</div>
					<div className="kpi-v tn">{stats?.queued ?? 0}</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">En cours</div>
					<div className="kpi-v tn">{stats?.running ?? 0}</div>
				</div>
				<div className="kpi success">
					<div className="kpi-l">Créés (total)</div>
					<div className="kpi-v tn">{stats?.done ?? 0}</div>
				</div>
				<div className="kpi forest">
					<div className="kpi-l">Échecs</div>
					<div className="kpi-v tn">{stats?.failed ?? 0}</div>
				</div>
			</div>

			{/* Composeur */}
			<div className="card">
				<div className="card-h">
					<div>
						<h3>
							Nouveau produit{" "}
							{isBulk && (
								<Tag variant="info" noDot>
									lot · {productCount} produits
								</Tag>
							)}
						</h3>
						<p className="sub muted">
							Un ou plusieurs produits — colle le texte ou importe des fichiers.
							Les fiches séparées par « ==== PRODUIT ==== » sont créées en lot ;
							les URLs d'images sont détectées automatiquement.
						</p>
					</div>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: 12.5,
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							checked={autoPublish}
							onChange={(e) => setAutoPublish(e.target.checked)}
						/>
						Publier automatiquement
					</label>
				</div>
				<div className="card-b">
					<textarea
						className="aform-i"
						value={rawText}
						onChange={(e) => setRawText(e.target.value)}
						onKeyDown={(e) => {
							if ((e.metaKey || e.ctrlKey) && e.key === "Enter") launch();
						}}
						placeholder={
							"Exemple :\nBlender mixeur SilverCrest 1000W, bol verre 1,5L, lames inox…\nDisponible en Rouge et Noir. Prix : 24 500 FCFA (au lieu de 32 000)\nhttps://exemple.com/photo-produit.jpg"
						}
						rows={8}
						style={{
							width: "100%",
							resize: "vertical",
							fontSize: 13,
							lineHeight: 1.55,
							fontFamily: "var(--a-font)",
						}}
						data-testid="ai-raw-text"
					/>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 10,
							marginTop: 10,
							flexWrap: "wrap",
						}}
					>
						<AdminButton
							variant="primary"
							onClick={launch}
							disabled={launching || rawText.trim().length < 20}
							data-testid="ai-launch"
						>
							<AdminIcon n={launching ? "refresh" : "star"} s={14} />
							{launching
								? "Lancement…"
								: isBulk
									? `Lancer la création de ${productCount} produits`
									: "Lancer la création"}
						</AdminButton>
						<input
							ref={fileRef}
							type="file"
							accept=".md,.txt,text/markdown,text/plain"
							multiple
							style={{ display: "none" }}
							onChange={(e) => void importFiles(e.target.files)}
						/>
						<AdminButton
							variant="ghost"
							onClick={() => fileRef.current?.click()}
							data-testid="ai-import"
						>
							<AdminIcon n="download" s={14} />
							Importer des fichiers
						</AdminButton>
						<span className="muted" style={{ fontSize: 11.5 }}>
							⌘⏎ pour lancer
						</span>
						<div style={{ flex: 1 }} />
						{detectedImages.length > 0 && (
							<span
								style={{
									display: "inline-flex",
									alignItems: "center",
									gap: 6,
									fontSize: 12,
								}}
							>
								<Tag variant="info" noDot>
									{detectedImages.length} image
									{detectedImages.length > 1 ? "s" : ""} détectée
									{detectedImages.length > 1 ? "s" : ""}
								</Tag>
								{detectedImages.slice(0, 4).map((u) => (
									<img
										key={u}
										src={u}
										alt=""
										style={{
											width: 34,
											height: 34,
											objectFit: "cover",
											borderRadius: 6,
											border: "1px solid var(--a-border)",
										}}
										onError={(e) =>
											((e.target as HTMLImageElement).style.opacity = "0.25")
										}
									/>
								))}
							</span>
						)}
					</div>
				</div>
			</div>

			{/* File d'attente */}
			<div className="card">
				<div className="card-h">
					<div>
						<h3>File d'attente</h3>
						<p className="sub muted">
							Les créations tournent en arrière-plan — tu peux enchaîner les
							lancements
						</p>
					</div>
					<div className="filter-bar" style={{ marginBottom: 0 }}>
						{FILTERS.map((f) => (
							<ChipFilter
								key={f.id}
								on={filter === f.id}
								onClick={() => setFilter(f.id)}
							>
								{f.label}
							</ChipFilter>
						))}
					</div>
				</div>
				<div
					className="card-b"
					style={{ padding: jobs.length === 0 ? undefined : 0 }}
				>
					{jobs.length === 0 ? (
						<p
							className="muted"
							style={{
								margin: 0,
								fontSize: 12.5,
								textAlign: "center",
								padding: 18,
							}}
						>
							Aucune création{" "}
							{filter !== "all"
								? `« ${FILTERS.find((f) => f.id === filter)?.label} »`
								: ""}{" "}
							pour l'instant — colle un texte ci-dessus et lance !
						</p>
					) : (
						jobs.map((j) => (
							<div
								key={j.id}
								style={{
									display: "grid",
									gridTemplateColumns: "auto 1fr auto",
									gap: 12,
									alignItems: "center",
									padding: "12px 16px",
									borderBottom: "1px solid var(--a-border)",
								}}
							>
								<Tag variant={STATUS_CLASS[j.status]}>
									{STATUS_LABEL[j.status]}
								</Tag>
								<div style={{ minWidth: 0 }}>
									<div
										onClick={() => setDetail(j)}
										title="Voir les détails"
										style={{
											fontSize: 13,
											fontWeight: 600,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											cursor: "pointer",
										}}
									>
										{jobTitle(j)}
									</div>
									<div
										className="muted"
										style={{
											fontSize: 11.5,
											marginTop: 2,
											display: "flex",
											alignItems: "center",
											gap: 8,
											flexWrap: "wrap",
										}}
									>
										<span>{timeAgo(j.createdAt)}</span>
										{j.imageUrls.length > 0 && (
											<span>
												· {j.imageUrls.length} image
												{j.imageUrls.length > 1 ? "s" : ""}
											</span>
										)}
										{j.status === "done" && j.productStatus && (
											<Tag
												variant={
													j.productStatus === "published"
														? "success"
														: "warning"
												}
												noDot
											>
												{j.productStatus === "published"
													? "Publié"
													: "Brouillon"}
											</Tag>
										)}
										{j.warnings.length > 0 && (
											<button
												type="button"
												onClick={() => setDetail(j)}
												style={{
													border: "none",
													background: "none",
													padding: 0,
													cursor: "pointer",
													font: "inherit",
													color: "var(--a-warning, #b7791f)",
													textDecoration: "underline dotted",
												}}
											>
												⚠ {j.warnings.length} avertissement
												{j.warnings.length > 1 ? "s" : ""}
											</button>
										)}
									</div>
									{j.status === "running" && (
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 10,
												marginTop: 6,
											}}
										>
											<div
												className="bar thin"
												style={{ flex: 1, maxWidth: 320 }}
											>
												<i style={{ width: `${j.progress}%` }} />
											</div>
											<span className="muted tn" style={{ fontSize: 11 }}>
												{STEP_LABEL[j.step ?? ""] ?? j.step} {j.progress}%
											</span>
										</div>
									)}
									{j.status === "failed" && j.error && (
										<div
											onClick={() => setDetail(j)}
											title="Voir le détail de l'erreur"
											style={{
												fontSize: 11.5,
												color: "var(--a-danger)",
												marginTop: 4,
												cursor: "pointer",
											}}
										>
											{j.error.slice(0, 180)}
											{j.error.length > 180 ? "…" : ""}
										</div>
									)}
								</div>
								<div style={{ display: "flex", gap: 6 }}>
									{j.status === "done" && j.productId && (
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() => navigate(`/catalogue/${j.productId}`)}
										>
											<AdminIcon n="eye" s={13} />
											Voir le produit
										</AdminButton>
									)}
									{/* Seulement quand le blocage vient d'une catégorie absente :
									    ailleurs, ce bouton n'aurait rien à créer. */}
									{j.status === "failed" &&
										/introuvable dans le catalogue/.test(j.error ?? "") && (
											<AdminButton
												variant="ghost"
												size="sm"
												onClick={() => setCategorieAcreer(j)}
											>
												<AdminIcon n="plus" s={13} />
												Créer la catégorie
											</AdminButton>
										)}
									{j.status === "failed" && (
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() => retryMut.mutate(j.id)}
											disabled={retryMut.isPending}
										>
											<AdminIcon n="refresh" s={13} />
											Relancer
										</AdminButton>
									)}
									{j.status !== "running" && (
										<button
											type="button"
											className="top-btn"
											onClick={() => void removeJob(j)}
											title="Retirer de la file"
											style={{ color: "var(--a-muted)" }}
										>
											<AdminIcon n="close" s={13} />
										</button>
									)}
								</div>
							</div>
						))
					)}
				</div>
				{total > TAILLE_PAGE && (
					<div
						style={{
							padding: "10px 14px",
							borderTop: "1px solid var(--a-border)",
						}}
					>
						<Pagination
							from={(page - 1) * TAILLE_PAGE + 1}
							to={Math.min(page * TAILLE_PAGE, total)}
							total={total}
							current={page}
							totalPages={totalPages}
							onChange={setPage}
						/>
					</div>
				)}
			</div>

			{categorieAcreer && (
				<CreateCategoryModal
					job={categorieAcreer}
					onClose={() => setCategorieAcreer(null)}
					onCreated={() => {
						setCategorieAcreer(null);
						invalidate();
					}}
				/>
			)}

			{detail && (
				<JobDetailsModal
					job={jobs.find((j) => j.id === detail.id) ?? detail}
					onClose={() => setDetail(null)}
					onView={(id) => {
						setDetail(null);
						navigate(`/catalogue/${id}`);
					}}
				/>
			)}
		</>
	);
}
